import { useEffect, useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { Button, Card, CardTitle, Field, Input, Select, Switch, useToast } from "@/core/ui";
import { ApiError } from "@/core/api/client";
import {
  useSaveTenantSettings,
  useSendTestEmail,
  useTenantSettings,
  type TenantSettings,
} from "./api";
import { GamesPicker } from "./GamesPicker";

export function TenantSettingsForm() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const settings = useTenantSettings();
  const save = useSaveTenantSettings();
  const testEmail = useSendTestEmail();
  const [form, setForm] = useState<TenantSettings | null>(null);
  useEffect(() => {
    if (settings.data) setForm(settings.data);
  }, [settings.data]);
  if (!form) return null;

  const submit = (e: FormEvent) => {
    e.preventDefault();
    save.mutate(form, {
      onSuccess: () => toast(t("admin.saved"), "success"),
      onError: () => toast(t("app.error_generic"), "danger"),
    });
  };
  const sendTestEmail = () =>
    testEmail.mutate(undefined, {
      onSuccess: ({ sent_to }) =>
        toast(t("admin.settings.test_email_sent", { email: sent_to }), "success"),
      onError: (error) => {
        const detail = error instanceof ApiError ? error.payload["detail"] : undefined;
        toast(
          typeof detail === "string" && detail
            ? t("admin.settings.test_email_failed", { detail })
            : t("app.error_generic"),
          "danger",
        );
      },
    });
  const setBranding = (key: keyof TenantSettings["branding"], value: string) =>
    setForm({ ...form, branding: { ...form.branding, [key]: value } });

  return (
    <form onSubmit={submit} className="bt-admin__panels">
      <Card>
        <CardTitle>{t("admin.settings.branding")}</CardTitle>
        <Field label={t("admin.settings.display_name")} htmlFor="display-name">
          <Input
            id="display-name"
            value={form.branding.display_name}
            onChange={(e) => setBranding("display_name", e.target.value)}
          />
        </Field>
        <Field label={t("admin.settings.logo_url")} htmlFor="logo-url">
          <Input
            id="logo-url"
            value={form.branding.logo_url}
            onChange={(e) => setBranding("logo_url", e.target.value)}
          />
        </Field>
        <div className="bt-admin__colors">
          {(["primary_color", "accent_color", "background_color"] as const).map((key) => (
            <Field key={key} label={t(`admin.settings.${key}`)} htmlFor={key}>
              <Input
                id={key}
                type="color"
                value={form.branding[key]}
                onChange={(e) => setBranding(key, e.target.value)}
                className="bt-admin__color-input"
              />
            </Field>
          ))}
        </div>
      </Card>
      <Card>
        <CardTitle>{t("admin.settings.behaviour")}</CardTitle>
        <Field
          label={t("admin.settings.cadence_cron")}
          htmlFor="cadence"
          hint={t("admin.settings.cadence_hint")}
        >
          <Input
            id="cadence"
            value={form.cadence_cron}
            onChange={(e) => setForm({ ...form, cadence_cron: e.target.value })}
          />
        </Field>
        <Field label={t("admin.settings.timezone")} htmlFor="timezone">
          <Input
            id="timezone"
            value={form.timezone}
            onChange={(e) => setForm({ ...form, timezone: e.target.value })}
          />
        </Field>
        <Field label={t("admin.settings.locale_default")} htmlFor="locale">
          <Select
            id="locale"
            value={form.locale_default}
            onChange={(e) => setForm({ ...form, locale_default: e.target.value })}
          >
            <option value="fr">Français</option>
            <option value="en">English</option>
          </Select>
        </Field>
        <Switch
          id="live-feedback"
          checked={form.live_feedback_enabled}
          onChange={(v) => setForm({ ...form, live_feedback_enabled: v })}
          label={t("admin.settings.live_feedback")}
        />
        <Switch
          id="solution-readback"
          checked={form.solution_readback_enabled}
          onChange={(v) => setForm({ ...form, solution_readback_enabled: v })}
          label={t("admin.settings.solution_readback")}
        />
        <p className="bt-admin__hint">{t("admin.settings.solution_readback_hint")}</p>
        <GamesPicker
          value={form.enabled_games}
          onChange={(keys) => setForm({ ...form, enabled_games: keys })}
        />
        <Switch
          id="notifications"
          checked={form.notifications_enabled}
          onChange={(v) => setForm({ ...form, notifications_enabled: v })}
          label={t("admin.settings.notifications")}
        />
        <div className="bt-admin__test-email">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            loading={testEmail.isPending}
            onClick={sendTestEmail}
          >
            {t("admin.settings.test_email")}
          </Button>
          <p className="bt-admin__hint">{t("admin.settings.test_email_hint")}</p>
        </div>
        <Button type="submit" className="bt-admin__submit" loading={save.isPending}>
          {t("admin.save")}
        </Button>
      </Card>
    </form>
  );
}
